<script setup lang="ts">
import ClusterPickList from '@src/pick/cluster-picklist/cluster-picklist.vue';
import { ClusterMinimal } from '../cluster/interfaces/cluster-minimal.ts';
import apiClient, { createApiErrorModal } from '@src/api/api-client.ts';
import { Picked } from '@src/pick/cluster-picklist/picked.ts';
import { closeLoader, openLoader } from '@utils/loader-util.ts';
import { PicklistLine } from '@src/pick/cluster-picklist/picklist-line.ts';
import TransferRequestPickedLines from '@src/transfer-request/transfer-request-picked-lines.vue';
import { useSingleRefComposable } from '@src/application/composables/single-ref-composable.ts';
import Headline from '@components/headline.vue';
import { WarehouseInterface } from '@src/warehouse/interfaces/warehouse-interface.ts';
import HorizontalAlignChildrenWrapper from '@components/layout-wrapper/horizontal-align-children-wrapper.vue';
import VerticalAlignChildrenWrapper from '@components/layout-wrapper/vertical-align-children-wrapper.vue';
import { createModalAbort } from '@utils/modal-util.ts';
import ScanBin from '@src/bin/component/scan-bin.vue';
import { useTransferRequestPickComposable } from '@src/transfer-request/composable/transfer-request-pick-composable.ts';
import { ApiError } from '@src/api/api-error';
import { usePickingCartComposable } from '@src/pick/picking-cart/composable/picking-cart-composable';

const emit = defineEmits<
    ((event: 'done') => void)
>();

async function loadPicklistLines(cluster: ClusterMinimal|null) {
    const request = apiClient.get('/be/transfer-request/pick/lines' + (cluster !== null ? '/' + cluster.id : ''));
    request.setCustomErrorHttpCodes(400);

    try {
        openLoader();
        const response = (await request).data;
        closeLoader();

        return response;
    } catch (e: any) {
        closeLoader();
        if (!(e instanceof ApiError)) {
            throw e;
        }

        if (e.code === 'NO_CURRENT_PICKING_CART') {
            await selectPickingCart();
            return loadPicklistLines(cluster);
        }

        await createApiErrorModal(e);
        throw e;
    }
}

async function pick(picked: Picked) {
    const request = apiClient.post('/be/transfer-request/pick', picked);
    request.setCustomErrorHttpCodes(400);

    try {
        openLoader();
        await request;
        closeLoader();
        await getPickedProductsRef()
            .loadPickedLines();
        await loadToPick();
    } catch (e: any) {
        closeLoader();
        if (!(e instanceof ApiError)) {
            throw e;
        }

        if (e.code === 'NO_CURRENT_PICKING_CART') {
            await selectPickingCart();
            return pick(picked);
        }

        await createApiErrorModal(e);
        throw e;
    }
}

const { selectPickingCart } = usePickingCartComposable();

async function pickSerial(serial: string) {
    const request = apiClient.post('/be/transfer-request/pick-serial/' + encodeURIComponent(serial));
    request.setCustomErrorHttpCodes(400);

    try {
        openLoader();
        await request;
        closeLoader();
        await getPickedProductsRef()
            .loadPickedLines();
        await loadToPick();
    } catch (e: any) {
        closeLoader();
        if (!(e instanceof ApiError)) {
            throw e;
        }

        if (e.code === 'NO_CURRENT_PICKING_CART') {
            await selectPickingCart();
            return pickSerial(serial);
        }

        await createApiErrorModal(e);
        throw e;
    }
}

async function notFound(picklistLine: PicklistLine) {
    openLoader();
    await apiClient.post('/be/transfer-request/not-found', {
        product: picklistLine.product,
        bin: picklistLine.bin,
    });
    await loadToPick();
    closeLoader();
}

async function abortPick() {
    openLoader();
    await apiClient.post('/be/transfer-request/pick/abort');
    closeLoader();

    emit('done');
}

const destinationWarehouse: WarehouseInterface = (await apiClient.get('/be/transfer-request/pick/destination')).data;

async function finishPick() {
    let idBin = null;
    if (!destinationWarehouse.oneBinWarehouse) {
        idBin = await createModalAbort<number>('Ziellagerfach wählen', ScanBin, 'update:model-value');
    }

    openLoader();
    await apiClient.post('/be/transfer-request/pick/finish' + (idBin !== null ? '/' + idBin : ''));
    closeLoader();

    emit('done');
}

const { setSingleTemplateRef: setPickedProductsRef, getSingleTemplateRef: getPickedProductsRef } = useSingleRefComposable();

const { loadClusters, toPick, loadToPick } = useTransferRequestPickComposable();

loadToPick();
</script>

<template>
    <VerticalAlignChildrenWrapper gap-bottom="1" avoid-outer-gap>
        <HorizontalAlignChildrenWrapper alignment="center">
            <Headline type="h2">Umlagerungsaufträge {{ destinationWarehouse.whsCode }} ({{ toPick }})</Headline>
        </HorizontalAlignChildrenWrapper>
        <ClusterPickList
            :load-clusters="loadClusters"
            :load-picklist-lines="loadPicklistLines"
            enable-abort
            enable-finish
            :on-pick="pick"
            :on-pick-serial="pickSerial"
            :on-not-found="notFound"
            @abort="abortPick"
            @finish="finishPick"
        >
            <template v-slot:picked-products>
                <TransferRequestPickedLines :ref="setPickedProductsRef"/>
            </template>
        </ClusterPickList>
    </VerticalAlignChildrenWrapper>
</template>
